/**
 * LogrosCard — Phase 10 dynamic-N refactor.
 *
 * Previously rendered exactly 9 hardcoded slots. Now the card mirrors whatever
 * the backend says (`/api/logros?date=…` returns an array of length 0..50),
 * adds:
 *   - "+ Add win" CTA at the bottom (disabled at MAX cap)
 *   - hover/swipe-revealed delete button per item (with confirm modal if the
 *     slot has user-typed text — empty slots delete silently)
 *   - inline edit (double-click on the text, Enter saves + jumps to next /
 *     creates one if last, Escape reverts, blur saves)
 *   - empty state at N=0 (icon + CTA "Add my first win")
 *
 * Perfect-day celebration is triggered server-side and only fires when
 * total >= 3 AND completed === total — UI just forwards the flag to
 * `triggerPerfectDay()`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  Loader2,
  Repeat,
  Sparkles,
  Plus,
  Trash2,
  ListChecks,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { getLocalDateString } from "@/lib/date";
import { useAgent } from "@/context/AgentContext";
import RoutinesSheet from "@/components/RoutinesSheet";

const MAX_LOGROS_PER_DAY = 50;

function getTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function AchievementItem({
  index,
  text,
  completed,
  onToggle,
  onEdit,
  onDeleteRequest,
  disabled,
  isLast,
  placeholder,
  editAriaLabel,
  deleteAriaLabel,
  editPlaceholder,
  autoEdit,
  onAutoEditConsumed,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) setDraft(text);
  }, [editing, text]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  // Fix (post-Fase 10): parent asks us to open edit mode automatically
  // after `POST /logros/item` so the user can type into the newly-added
  // slot without needing a double-click. `onAutoEditConsumed` lets the
  // parent clear its one-shot flag once we've honored the request.
  useEffect(() => {
    if (autoEdit && !editing) {
      setEditing(true);
      if (typeof onAutoEditConsumed === "function") onAutoEditConsumed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoEdit]);

  const commit = useCallback(() => {
    const next = (draft || "").trim();
    setEditing(false);
    if (next !== (text || "")) onEdit(next);
  }, [draft, text, onEdit]);

  const cancel = useCallback(() => {
    setDraft(text);
    setEditing(false);
  }, [text]);

  const handleKey = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        commit();
        // Bubble up "next" so the parent can jump focus to the next row.
        if (inputRef.current) inputRef.current.dataset.intent = "next";
      } else if (e.key === "Escape") {
        e.preventDefault();
        cancel();
      }
    },
    [commit, cancel],
  );

  const displayText =
    text && text.trim().length > 0 ? text : placeholder || "";

  return (
    <li
      className={`group relative ${isLast ? "" : "border-b border-white/[0.04]"}`}
      data-testid={`logro-row-${index + 1}`}
    >
      <div className="flex items-center gap-4 py-3.5 px-1 rounded-xl hover:bg-white/[0.025]">
        <button
          type="button"
          onClick={onToggle}
          disabled={disabled || editing}
          aria-pressed={completed}
          data-testid={`logro-toggle-${index + 1}`}
          className="
            flex items-center gap-4 flex-1 min-w-0
            transition-colors duration-200
            disabled:opacity-60 disabled:cursor-not-allowed
            focus:outline-none
          "
        >
          <span
            className={`
              font-mono font-semibold tabular-nums w-7 shrink-0 text-center text-[20px]
              transition-all duration-300
              ${completed
                ? "text-[#00E5FF]/40"
                : "text-[#00E5FF] [text-shadow:0_0_10px_rgba(0,229,255,0.45)]"}
            `}
            data-testid={`logro-number-${index + 1}`}
          >
            {index + 1}
          </span>

          <span
            className={`
              relative h-6 w-6 shrink-0 rounded-full
              flex items-center justify-center
              border transition-all duration-300
              ${completed
                ? "bg-[#00E5FF] border-[#00E5FF] shadow-[0_0_12px_rgba(0,229,255,0.55)]"
                : "bg-transparent border-[rgba(0,229,255,0.35)] group-hover:border-[rgba(0,229,255,0.6)]"}
            `}
            data-testid={`logro-checkbox-${index + 1}`}
            data-checked={completed ? "true" : "false"}
          >
            <Check
              className={`
                h-3.5 w-3.5 text-[#0A0E1A] transition-all duration-300
                ${completed ? "scale-100 opacity-100" : "scale-50 opacity-0"}
              `}
              strokeWidth={3.5}
            />
          </span>
        </button>

        <div className="flex-1 min-w-0">
          {editing ? (
            <input
              ref={inputRef}
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commit}
              onKeyDown={handleKey}
              placeholder={editPlaceholder}
              maxLength={120}
              data-testid={`logro-edit-input-${index + 1}`}
              aria-label={editAriaLabel}
              className="
                w-full bg-transparent
                text-[14px] sm:text-[15px] text-[#E5E7EB]
                border-b border-[rgba(0,229,255,0.45)]
                focus:outline-none focus:border-[#00E5FF]
                placeholder:text-[#6B7280] placeholder:italic
                py-0.5
              "
            />
          ) : (
            <button
              type="button"
              onDoubleClick={() => setEditing(true)}
              data-testid={`logro-label-${index + 1}`}
              className={`
                text-left w-full text-[14px] sm:text-[15px] leading-snug
                transition-all duration-300 cursor-text
                ${completed
                  ? "text-[#9CA3AF] line-through opacity-60"
                  : "text-[#E5E7EB]"}
                ${(!text || text.trim().length === 0) ? "italic opacity-70" : ""}
              `}
            >
              {displayText}
            </button>
          )}
        </div>

        {/* Delete button — visible on hover/focus-within only. */}
        <button
          type="button"
          onClick={() => onDeleteRequest(index + 1, text)}
          disabled={disabled || editing}
          data-testid={`logro-delete-${index + 1}`}
          aria-label={deleteAriaLabel}
          className="
            opacity-0 group-hover:opacity-100 group-focus-within:opacity-100
            transition-opacity
            h-7 w-7 rounded-full
            flex items-center justify-center
            text-[#9CA3AF] hover:text-[#F87171]
            hover:bg-[rgba(248,113,113,0.08)]
            disabled:opacity-30 disabled:hover:bg-transparent
          "
        >
          <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
        </button>
      </div>
    </li>
  );
}

function DeleteConfirmModal({ open, onCancel, onConfirm, title, message, ctaLabel, cancelLabel }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;
  return (
    <div
      data-testid="logro-delete-confirm-overlay"
      className="fixed inset-0 z-[10080] flex items-center justify-center bg-black/65 backdrop-blur-sm animate-fade-in-up px-4"
      role="dialog"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className="
          relative w-full max-w-sm
          rounded-[20px] bg-[#0F1525]
          border border-[rgba(248,113,113,0.35)]
          p-5 text-[#E5E7EB]
          shadow-[0_30px_80px_rgba(0,0,0,0.7)]
        "
        data-testid="logro-delete-confirm-modal"
      >
        <h3 className="text-[16px] font-semibold mb-1">{title}</h3>
        <p className="text-[12.5px] text-[#9CA3AF] mb-4">{message}</p>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onCancel}
            data-testid="logro-delete-cancel"
            className="
              h-10 rounded-full
              bg-white/[0.03] border border-white/10
              text-[#E5E7EB] text-[13px]
              hover:bg-white/[0.06]
            "
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            data-testid="logro-delete-confirm"
            className="
              h-10 rounded-full
              bg-[rgba(248,113,113,0.15)]
              border border-[rgba(248,113,113,0.45)]
              text-[#F87171] text-[13px] font-medium
              hover:bg-[rgba(248,113,113,0.25)]
            "
          >
            {ctaLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onAdd, t, disabled }) {
  return (
    <div
      className="
        py-10 flex flex-col items-center text-center gap-3
      "
      data-testid="logros-empty-state"
    >
      <div className="h-14 w-14 rounded-full nx-glass flex items-center justify-center">
        <ListChecks
          className="h-7 w-7 text-[#00E5FF] [filter:drop-shadow(0_0_8px_rgba(0,229,255,0.55))]"
          strokeWidth={1.75}
        />
      </div>
      <p className="text-[15px] font-semibold text-[#E5E7EB]">
        {t("logros.empty_state_title")}
      </p>
      <p className="text-[12.5px] text-[#9CA3AF] max-w-[260px] leading-relaxed">
        {t("logros.empty_state_subtitle")}
      </p>
      <button
        type="button"
        onClick={onAdd}
        disabled={disabled}
        data-testid="logros-empty-state-cta"
        className="
          mt-2 inline-flex items-center gap-2
          h-10 px-5 rounded-full
          bg-gradient-to-r from-[rgba(0,229,255,0.18)] to-[rgba(0,229,255,0.08)]
          border border-[rgba(0,229,255,0.5)]
          text-[#00E5FF] text-[13px] font-medium
          hover:from-[rgba(0,229,255,0.28)]
          disabled:opacity-60
        "
      >
        {t("logros.empty_state_cta")}
      </button>
    </div>
  );
}

export default function LogrosCard() {
  const { t } = useTranslation();
  const date = useMemo(() => getLocalDateString(), []);

  // Phase 10: placeholders come from i18n as an array. They are ONLY shown
  // when a slot's text is empty AND we're inside the legacy 9-default range.
  // If the user prefers fewer/more slots, the empty ones simply show no
  // placeholder hint — keeping the UI honest about what's actually saved.
  const placeholders = useMemo(() => {
    const v = t("logros.placeholders.default", { returnObjects: true });
    return Array.isArray(v) ? v : [];
  }, [t]);

  const [logros, setLogros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [maxToast, setMaxToast] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { slot, hadText }
  const [busy, setBusy] = useState(false);
  // Fix (post-Fase 10): after POST /logros/item, we tell the newly-appended
  // row to open its inline editor automatically. The child clears the flag
  // via `onAutoEditConsumed` so subsequent add clicks re-arm the signal.
  const [autoEditIndex, setAutoEditIndex] = useState(null);
  const mountedRef = useRef(true);
  const toastTimerRef = useRef(null);
  const { triggerPerfectDay, onLogrosSync } = useAgent();

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  // Listen for the agent dock pushing new logros. Phase 10: accept ANY length.
  useEffect(() => {
    const unsub = onLogrosSync((newLogros) => {
      if (Array.isArray(newLogros) && mountedRef.current) {
        setLogros(newLogros);
      }
    });
    return unsub;
  }, [onLogrosSync]);

  const showToast = useCallback((msg) => {
    setToast(msg);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => {
      if (mountedRef.current) setToast(null);
    }, 2500);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/logros?date=${encodeURIComponent(date)}`);
        if (!cancelled) {
          const items = Array.isArray(res.data?.logros) ? res.data.logros : [];
          setLogros(items);
        }
      } catch {
        if (!cancelled) setError(t("logros.loading_error"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [date, t]);

  const handlePerfect = useCallback(
    (response) => {
      if (response?.is_perfect_day_today) {
        // Phase 10: pass the day's total logros so the celebration banner
        // & TTS render "{N} de {N} logros" instead of the fixed 9.
        const total = Array.isArray(response.logros) ? response.logros.length : undefined;
        triggerPerfectDay(date, total);
      }
    },
    [triggerPerfectDay, date],
  );

  const toggle = useCallback(
    async (idx) => {
      const current = logros[idx];
      if (!current) return;
      const nextCompleted = !current.completed;
      setLogros((prev) =>
        prev.map((l, i) => (i === idx ? { ...l, completed: nextCompleted } : l)),
      );
      try {
        const res = await api.patch(
          `/logros/item?tz=${encodeURIComponent(getTimezone())}`,
          { date, slot: idx + 1, completed: nextCompleted },
        );
        if (Array.isArray(res.data?.logros) && mountedRef.current) {
          setLogros(res.data.logros);
        }
        handlePerfect(res.data);
      } catch {
        if (mountedRef.current) {
          setLogros((prev) =>
            prev.map((l, i) => (i === idx ? { ...l, completed: !nextCompleted } : l)),
          );
          setError(t("logros.save_error"));
          setTimeout(() => mountedRef.current && setError(null), 2500);
        }
      }
    },
    [logros, date, handlePerfect, t],
  );

  const editText = useCallback(
    async (idx, nextText) => {
      setLogros((prev) =>
        prev.map((l, i) => (i === idx ? { ...l, text: nextText } : l)),
      );
      try {
        const res = await api.patch(
          `/logros/item?tz=${encodeURIComponent(getTimezone())}`,
          { date, slot: idx + 1, text: nextText },
        );
        if (Array.isArray(res.data?.logros) && mountedRef.current) {
          setLogros(res.data.logros);
        }
      } catch {
        if (mountedRef.current) {
          setError(t("logros.save_error"));
          setTimeout(() => mountedRef.current && setError(null), 2500);
        }
      }
    },
    [date, t],
  );

  const addItem = useCallback(async () => {
    if (busy) return;
    if (logros.length >= MAX_LOGROS_PER_DAY) {
      setMaxToast(true);
      setTimeout(() => mountedRef.current && setMaxToast(false), 2500);
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/logros/item", { date, text: "" });
      if (Array.isArray(res.data?.logros) && mountedRef.current) {
        const newList = res.data.logros;
        setLogros(newList);
        // The backend also returns `new_slot` (1-based). Convert to 0-based
        // index and arm the auto-edit trigger for that row.
        const newSlot = typeof res.data?.new_slot === "number"
          ? res.data.new_slot - 1
          : newList.length - 1;
        setAutoEditIndex(newSlot);
      }
      handlePerfect(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "logros_count_invalid") {
        setMaxToast(true);
        setTimeout(() => mountedRef.current && setMaxToast(false), 2500);
      } else {
        setError(t("logros.save_error"));
        setTimeout(() => mountedRef.current && setError(null), 2500);
      }
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  }, [busy, logros.length, date, handlePerfect, t]);

  const requestDelete = useCallback((slot, hadText) => {
    // Empty slots delete silently — no confirm modal needed.
    if (!hadText || !String(hadText).trim()) {
      doDelete(slot);
    } else {
      setDeleteTarget({ slot, hadText });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doDelete = useCallback(
    async (slot) => {
      setBusy(true);
      try {
        const res = await api.delete("/logros/item", { data: { date, slot } });
        if (Array.isArray(res.data?.logros) && mountedRef.current) {
          setLogros(res.data.logros);
        }
        handlePerfect(res.data);
      } catch {
        if (mountedRef.current) {
          setError(t("logros.save_error"));
          setTimeout(() => mountedRef.current && setError(null), 2500);
        }
      } finally {
        if (mountedRef.current) {
          setBusy(false);
          setDeleteTarget(null);
        }
      }
    },
    [date, handlePerfect, t],
  );

  const completed = logros.filter((l) => l.completed).length;
  const total = logros.length;
  const progressDenominator = Math.max(total, 1);
  const progress = total === 0 ? 0 : (completed / progressDenominator) * 100;
  const isEmpty = !loading && total === 0;
  const atCap = total >= MAX_LOGROS_PER_DAY;

  return (
    <section
      className="nx-glass rounded-[24px] px-5 sm:px-6 pt-6 pb-3 animate-fade-in-up relative"
      data-testid="logros-card"
    >
      <div className="flex items-end justify-between gap-3 mb-5">
        <div className="min-w-0">
          <h2
            className="text-[20px] sm:text-[22px] font-semibold tracking-tight text-[#E5E7EB]"
            data-testid="logros-title"
          >
            {total > 0
              ? t("logros.title_dynamic", { n: total })
              : t("logros.title_empty", { defaultValue: t("logros.title") })}
          </h2>
          <p className="text-[12.5px] text-[#9CA3AF] mt-0.5">
            {t("logros.subtitle")}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            data-testid="logros-open-routines"
            aria-label={t("logros.routines_aria")}
            className="
              h-9 px-3 rounded-full
              border border-[rgba(0,229,255,0.3)] bg-white/[0.02]
              text-[11px] uppercase tracking-[0.16em] text-[#00E5FF]
              hover:bg-[rgba(0,229,255,0.06)] hover:border-[rgba(0,229,255,0.5)]
              flex items-center gap-1.5 transition-colors
            "
          >
            <Repeat className="h-3.5 w-3.5" strokeWidth={2} />
            {t("logros.routines_button")}
          </button>
          <div className="text-right" data-testid="logros-counter-block">
            {loading ? (
              <Loader2
                className="h-4 w-4 text-[#00E5FF]/70 animate-spin ml-auto"
                strokeWidth={1.75}
              />
            ) : (
              <>
                <p
                  className="font-mono text-[15px] font-semibold tabular-nums text-[#00E5FF] [text-shadow:0_0_10px_rgba(0,229,255,0.4)]"
                  data-testid="logros-counter"
                >
                  {completed} / {total}
                </p>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF] mt-0.5">
                  {t("logros.completed_label")}
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      <div
        className="h-[3px] w-full rounded-full bg-white/[0.05] overflow-hidden mb-2"
        data-testid="logros-progress-track"
      >
        <div
          className="h-full bg-[#00E5FF] shadow-[0_0_10px_rgba(0,229,255,0.7)] transition-[width] duration-500 ease-out"
          style={{ width: `${progress}%` }}
          data-testid="logros-progress-fill"
        />
      </div>

      {error ? (
        <p
          className="mt-2 text-[11.5px] text-[#F87171] text-center"
          data-testid="logros-error"
        >
          {error}
        </p>
      ) : null}

      {isEmpty ? (
        <EmptyState onAdd={addItem} t={t} disabled={busy} />
      ) : (
        <ul className="-mx-1" data-testid="logros-list">
          {logros.map((item, i) => (
            <AchievementItem
              key={i}
              index={i}
              text={item.text}
              completed={!!item.completed}
              isLast={i === total - 1}
              disabled={loading || busy}
              onToggle={() => toggle(i)}
              onEdit={(next) => editText(i, next)}
              onDeleteRequest={requestDelete}
              placeholder={placeholders[i] || ""}
              editAriaLabel={t("logros.edit_aria")}
              deleteAriaLabel={t("logros.delete_button_aria")}
              editPlaceholder={t("logros.edit_placeholder")}
              autoEdit={autoEditIndex === i}
              onAutoEditConsumed={() => setAutoEditIndex(null)}
            />
          ))}
        </ul>
      )}

      {/* + Add win — hidden in empty state because the empty CTA covers it */}
      {!isEmpty ? (
        <div className="mt-3 pb-1 flex justify-center">
          <button
            type="button"
            onClick={addItem}
            disabled={busy || atCap}
            data-testid="logros-add-button"
            title={atCap ? t("logros.max_reached") : ""}
            className="
              inline-flex items-center gap-1.5
              h-9 px-4 rounded-full
              border border-[rgba(0,229,255,0.3)] bg-white/[0.02]
              text-[12px] text-[#00E5FF]
              hover:bg-[rgba(0,229,255,0.08)] hover:border-[rgba(0,229,255,0.55)]
              disabled:opacity-40 disabled:cursor-not-allowed
              transition-colors
            "
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
            {t("logros.add_button")}
          </button>
        </div>
      ) : null}

      {(toast || maxToast) ? (
        <div
          className="
            absolute left-1/2 -translate-x-1/2 bottom-3
            px-3.5 py-1.5 rounded-full
            bg-[rgba(0,229,255,0.12)] border border-[rgba(0,229,255,0.4)]
            text-[12px] text-[#00E5FF] font-medium
            shadow-[0_0_18px_rgba(0,229,255,0.3)]
            flex items-center gap-1.5
            animate-fade-in-up
          "
          data-testid="logros-toast"
        >
          <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
          {maxToast ? t("logros.max_reached") : toast}
        </div>
      ) : null}

      <DeleteConfirmModal
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && doDelete(deleteTarget.slot)}
        title={t("logros.delete_confirm_title")}
        message={t("logros.delete_confirm_message")}
        ctaLabel={t("logros.delete_confirm_cta")}
        cancelLabel={t("common.cancel")}
      />

      <RoutinesSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        currentLogros={logros}
        onApplied={(newLogros) => {
          // Phase 10: routines can now have any length 0..50.
          if (Array.isArray(newLogros)) setLogros(newLogros);
        }}
        onToast={(msg) => showToast(msg)}
      />
    </section>
  );
}
